// design/OpsJobs.dc.html. SD4: "trivially inspectable from the operator console." GET
// /ops/jobs?status=..., POST /ops/jobs/{id}/retry.
import { useCallback, useEffect, useState } from "react";
import { api, ApiFail } from "../api/client";
import type { JobStatus, ListResponse, OpsJobOut } from "../api/types";
import { botStatusIcon, botStatusLabel, botStatusTone } from "../calendar/botStatus";
import { Notice } from "../components/ui";
import "../admin/admin.css";
import { Badge, Button, Card, Select } from "@grackle/ui";

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "failed,dead", label: "Failed / dead" },
  { value: "queued,running", label: "Queued / running" },
  { value: "done", label: "Done" },
];

function clock(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function OpsJobsPage() {
  const [status, setStatus] = useState(STATUS_FILTERS[0].value);
  const [jobs, setJobs] = useState<OpsJobOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  const load = useCallback(() => {
    api<ListResponse<OpsJobOut>>("GET", `/ops/jobs?status=${status}`)
      .then((res) => setJobs(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load jobs."));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  async function onRetry(jobId: string) {
    setRetrying(jobId);
    setError(null);
    try {
      await api("POST", `/ops/jobs/${jobId}/retry`);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't retry that job.");
    } finally {
      setRetrying(null);
    }
  }

  const retryable = (s: JobStatus) => s === "failed" || s === "dead";

  return (
    <>
      <header className="page-head">
        <div className="page-head__grow">
          <h1>Jobs</h1>
          {/* SD4: the queue is a Postgres table (SKIP LOCKED), not a broker. */}
          <p>Background jobs such as processing, transcription, notetaker joins and deletions.</p>
        </div>
        <Select aria-label="Status" value={status} onChange={setStatus} options={STATUS_FILTERS} />
      </header>

      {error ? <Notice tone="error">{error}</Notice> : null}

      {jobs === null ? (
        <div className="gk-sm">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="gk-sm">Nothing in this state.</div>
      ) : (
        <Card size="flush" className="admin-table-card">
<table className="admin-table">
          <thead>
            <tr>
              <th className="gk-overline">Kind</th>
              <th className="gk-overline">Status</th>
              <th className="gk-overline num">Attempts</th>
              <th className="gk-overline">Run after</th>
              <th className="gk-overline">Last error</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id}>
                <td>
                  <div className="admin-row-name mono">{j.kind}</div>
                  <div className="admin-row-sub mono">{j.id.slice(0, 8)}</div>
                  {/* F9 / SD41: a notetaker_join job's bot_status, kept here rather than its own
                      column so every other job kind's row stays unchanged. */}
                  {j.bot_status ? (
                    <Badge tone={botStatusTone(j.bot_status)} icon={botStatusIcon(j.bot_status)} size="sm">
                      {botStatusLabel(j.bot_status)}
                    </Badge>
                  ) : null}
                </td>
                <td className="mono">{j.status}</td>
                <td className="num">
                  {j.attempts}/{j.max_attempts}
                </td>
                <td className="mono">{clock(j.run_after)}</td>
                <td className="ops-error">{j.last_error ?? "—"}</td>
                <td>
                  {retryable(j.status) ? (
                    <Button
                      variant="secondary" size="sm"
                      disabled={retrying === j.id}
                      onClick={() => onRetry(j.id)}
                    >
                      {retrying === j.id ? "Retrying…" : "Retry"}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
</Card>
      )}
    </>
  );
}
